import { useCallback, useEffect, useRef } from "react"
import { DateTime } from "luxon"
import type { FieldMetaProps } from "formik"
import { useField, useFormikContext } from "formik"
import { Stack } from "@mui/material"
import { TimePicker } from "@mui/x-date-pickers"

import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import type { DeliveryTimesField, MeetingServiceTabValues } from "../../form/formTypes"
import { DynamicConfigKey } from "../../form/formTypes"
import type { NullableCalendarEvent } from "../../resource/calendar/fullCalendarTypes"
import {
    createInitEvents,
    DEFAULT_DURATION_MINUTES,
    DEFAULT_MINIMUM_DURATION_MINUTES,
    getMinAllowedEndTime,
} from "../../resource/resourceBookingCalendars/helpers"
import { getFormikFieldError } from "../../form/helpers"
import { roundToNearest5Minutes } from "../../helpers"

type MeetingTimeFieldsProps = Readonly<{
    startLabel: string
    endLabel: string
    // A legacy single-field config validates on its own key, not on MEETING_START_TIME, so its
    // required error would otherwise never reach either picker.
    legacyMeta?: FieldMetaProps<DateTime | null>
}>

export function MeetingTimeFields({ startLabel, endLabel, legacyMeta }: MeetingTimeFieldsProps) {
    const dateTimeFactory = useDateTime()
    const now = dateTimeFactory.now()

    const { submitCount } = useFormikContext<MeetingServiceTabValues>()

    const [{ value: currentStart }, startMeta, startHelpers] = useField<DateTime | null>(
        DynamicConfigKey.MEETING_START_TIME,
    )
    const [{ value: currentEnd }, endMeta, endHelpers] = useField<DateTime | null>(DynamicConfigKey.MEETING_END_TIME)
    const [{ value: event }, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [{ value: multipleDeliveries }] = useField<DeliveryTimesField | undefined>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const eventStart = event?.start ? dateTimeFactory.fromISO(event.start) : null
    const eventEnd = event?.end ? dateTimeFactory.fromISO(event.end) : null

    const isOwnedByBooking = !!event?.resourceIds.length

    const deliveryDate = multipleDeliveries?.deliveryDate
    const deliveryTime = multipleDeliveries?.deliveryTimes?.[0]?.time

    const deliveryDateTime =
        deliveryDate && deliveryTime ? dateTimeFactory.fromISO(`${deliveryDate}T${deliveryTime}`) : null

    // An empty TimePicker builds a new value on today unless it is given another reference day.
    const deliveryDay = deliveryDateTime ?? (deliveryDate ? dateTimeFactory.fromISO(deliveryDate) : null)

    const previousDeliveryDateRef = useRef(deliveryDate)

    const writeEvent = useCallback(
        (start: DateTime, end: DateTime) => {
            eventHelpers.setValue(
                createInitEvents(
                    [],
                    dateTimeFactory.toApiISOMinutesFormat(start),
                    dateTimeFactory.toApiISOMinutesFormat(end),
                ),
            )
        },
        [dateTimeFactory, eventHelpers],
    )

    useEffect(() => {
        const shouldInitFromEvent =
            !!eventStart && !!eventEnd && (!currentStart?.equals?.(eventStart) || !currentEnd?.equals?.(eventEnd))

        const shouldInitFromDelivery =
            !eventStart &&
            !eventEnd &&
            multipleDeliveries?.activeTime &&
            deliveryDateTime &&
            (!currentStart || !currentEnd)

        if (!shouldInitFromEvent && !shouldInitFromDelivery) return

        const fallbackStart = deliveryDateTime && deliveryDateTime > now ? deliveryDateTime : now

        // The timeline event owns its own time (a booking may start at any minute, e.g. right on the
        // now-indicator), so it is taken as is — re-rounding would show a start the booking doesn't have.
        const start =
            shouldInitFromEvent && eventStart ? eventStart : (currentStart ?? roundToNearest5Minutes(fallbackStart))
        const end =
            shouldInitFromEvent && eventEnd
                ? eventEnd
                : currentEnd && currentEnd > start
                  ? currentEnd
                  : start.plus({ minutes: DEFAULT_DURATION_MINUTES })

        startHelpers.setValue(start)
        endHelpers.setValue(end)

        if (!eventStart || !eventEnd) {
            writeEvent(start, end)
        }
    }, [
        multipleDeliveries?.activeTime,
        deliveryDateTime?.toISO(),
        eventStart?.toISO(),
        eventEnd?.toISO(),
        currentStart?.toISO?.(),
        currentEnd?.toISO?.(),
    ])

    useEffect(() => {
        const previousDeliveryDate = previousDeliveryDateRef.current
        previousDeliveryDateRef.current = deliveryDate

        // Only a delivery date the user moves pulls the meeting with it. A saved order keeps the date it
        // was saved with — catering may legitimately be delivered on another day than the meeting.
        if (previousDeliveryDate === deliveryDate) return
        if (!deliveryDate || isOwnedByBooking) return
        if (!currentStart || !currentEnd) return

        const target = dateTimeFactory.fromISO(deliveryDate)
        if (currentStart.hasSame(target, "day")) return

        const onTargetDay = (value: DateTime) => value.set({ year: target.year, month: target.month, day: target.day })

        const nextStart = onTargetDay(currentStart)
        const nextEnd = onTargetDay(currentEnd)

        startHelpers.setValue(nextStart)
        endHelpers.setValue(nextEnd)
        writeEvent(nextStart, nextEnd)
    }, [deliveryDate, isOwnedByBooking])

    const startErrorProps = getFormikFieldError(
        startMeta.error || !legacyMeta?.error ? startMeta : legacyMeta,
        submitCount,
    )
    const endErrorProps = getFormikFieldError(endMeta, submitCount)

    // TimePicker bounds compare the time of day only, so `now` may bound the meeting only while it is today.
    const meetingDay = currentStart ?? deliveryDateTime ?? now
    const isMeetingToday = meetingDay.hasSame(now, "day")

    const minStartTime = isMeetingToday ? now : undefined

    // For a meeting that has already started the start-based bound lies in the past,
    // so the end can never be moved before the minimum step from now.
    const minEndTime = currentStart
        ? isMeetingToday
            ? DateTime.max(
                  currentStart.plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES }),
                  getMinAllowedEndTime(dateTimeFactory),
              )
            : currentStart.plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES })
        : minStartTime?.plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES })

    const handleStartChange = useCallback(
        (value: DateTime | null) => {
            if (!value) {
                startHelpers.setValue(null)
                return
            }
            const roundedStart = roundToNearest5Minutes(value)
            startHelpers.setValue(roundedStart)

            if (!currentEnd || roundedStart > currentEnd) {
                const roundedEnd = roundedStart.plus({ minutes: DEFAULT_DURATION_MINUTES })
                endHelpers.setValue(roundedEnd)
                writeEvent(roundedStart, roundedEnd)
            } else {
                writeEvent(roundedStart, currentEnd)
            }
        },
        [currentEnd, startHelpers, endHelpers, writeEvent],
    )

    const handleEndChange = useCallback(
        (value: DateTime | null) => {
            if (!value) {
                endHelpers.setValue(null)
                return
            }
            const roundedEnd = roundToNearest5Minutes(value)
            endHelpers.setValue(roundedEnd)

            if (!currentStart) return
            writeEvent(currentStart, roundedEnd)
        },
        [currentStart, endHelpers, writeEvent],
    )

    return (
        <Stack direction="row" gap="1rem">
            <TimePicker
                value={currentStart ?? null}
                label={startLabel}
                format="HH:mm"
                ampm={false}
                minutesStep={5}
                minTime={minStartTime}
                referenceDate={deliveryDay ?? undefined}
                onChange={handleStartChange}
                disabled={isOwnedByBooking}
                slotProps={{
                    // Two pickers share a drawer row, so an empty field's label would run under the clock icon.
                    textField: {
                        fullWidth: true,
                        size: "small",
                        InputLabelProps: { shrink: true },
                        ...startErrorProps,
                    },
                }}
            />
            <TimePicker
                value={currentEnd ?? null}
                label={endLabel}
                format="HH:mm"
                ampm={false}
                minutesStep={5}
                minTime={minEndTime}
                referenceDate={currentStart ?? deliveryDay ?? undefined}
                onChange={handleEndChange}
                disabled={isOwnedByBooking}
                slotProps={{
                    textField: { fullWidth: true, size: "small", InputLabelProps: { shrink: true }, ...endErrorProps },
                }}
            />
        </Stack>
    )
}
